import type { PartialMessage, PlainMessage } from '@bufbuild/protobuf';
import { isEqual } from 'lodash-es';

import {
  NoteAddedBlockOp,
  NoteLineOp,
  NoteSectionOp,
  type DocumentAnswerChange,
  type NoteLineChange,
} from '@/shared/api/protocol-ts/api/scribe/svc_encounter_pb';
import type {
  NoteNumberValue,
  NoteTableRowValue,
  NoteTeethGroup,
  NoteToothMark,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import {
  DocumentAnswerSource,
  NoteLineStatus,
  type DocumentAnswer,
  type DocumentAnswers as NoteAnswers,
} from '@/shared/api/protocol-ts/model/dto_scribe_encounter_pb';
import type {
  DocumentAnswers,
  NoteAnswersUpdate,
} from '@/shared/api/transcriptor';

import type { NoteTextEdit } from '@/entities/templates';

import { type AnswerEdit } from '@/features/audioRecording';

import type {
  NoteAssumption,
  NoteLineResetParts,
  SurfaceSection,
} from './noteSurface';
import {
  findNoteAnswer,
  findNoteOwnOptions,
  isNoteLineShown,
  noteBlockEditKey,
  readNoteTableRows,
  type NoteBlockDef,
  type NoteBlockSelection,
} from './noteView';

// One clinician operation on a block note, as scribe takes it. Edits of the
// same block (or the same line note, or section order) replace an unsent one;
// every other operation is sent as it is.
export type NoteOperation = {
  key: string;
  update: NoteAnswersUpdate;
  // The lines it changes, one per block change: its inverse names them, and
  // they stop showing as new.
  lineIds: string[];
  // Text typed into the note's document: one step with the typing before it
  // into the same text.
  typing?: boolean;
};

export type NoteValue = {
  values?: string[];
  text?: string;
  teeth?: string[];
  teethGroups?: NoteTeethGroup[];
  // Only a date field takes it.
  date?: string;
  // Only a teeth field that stores marks takes them: the whole set.
  marks?: PlainMessage<NoteToothMark>[];
  // The explicit "none", alone.
  none?: boolean;
  // Only a number field takes them: its values.
  numbers?: PlainMessage<NoteNumberValue>[];
  // Only a table field takes them: the whole table.
  tableRows?: PlainMessage<NoteTableRowValue>[];
};

const uniqueKey = () => `op:${crypto.randomUUID()}`;

const blockChange = (
  blockId: string,
  lineId: string,
  change: Omit<PartialMessage<DocumentAnswerChange>, 'Key'>,
): NoteOperation => ({
  key: noteBlockEditKey(blockId),
  update: { Changes: [{ ...change, Key: { BlockID: blockId } }] },
  lineIds: [lineId],
});

export const setNoteBlock = (
  blockId: string,
  lineId: string,
  value: NoteValue,
) =>
  blockChange(blockId, lineId, {
    Values: value.values ?? [],
    Text: value.text ?? '',
    Teeth: value.teeth ?? [],
    TeethGroups: value.teethGroups ?? [],
    ...(value.date !== undefined && { Date: value.date }),
    ...(value.marks !== undefined && { Marks: value.marks }),
    ...(value.none && { None: true }),
    ...(value.numbers !== undefined && { Numbers: value.numbers }),
    ...(value.tableRows !== undefined && { TableRows: value.tableRows }),
  });

// One request of several operations' block and line changes. Its line ids
// list the block changes' lines first, in the order of its changes.
export const combineNoteOperations = (
  operations: NoteOperation[],
): NoteOperation => {
  const lineChanges = operations.flatMap(
    (operation) => operation.update.LineChanges ?? [],
  );
  const [blockOperations, lineOperations] = [
    operations.filter((operation) => !operation.update.LineChanges?.length),
    operations.filter((operation) => !!operation.update.LineChanges?.length),
  ];
  return {
    key: uniqueKey(),
    update: {
      Changes: operations.flatMap(
        (operation) => operation.update.Changes ?? [],
      ),
      ...(lineChanges.length > 0 && { LineChanges: lineChanges }),
    },
    lineIds: [...blockOperations, ...lineOperations].flatMap(
      (operation) => operation.lineIds,
    ),
  };
};

// Confirming a value AI assumed is the manual set of the same value.
export const confirmNoteBlock = (
  blockId: string,
  lineId: string,
  selection: NoteBlockSelection,
) =>
  setNoteBlock(blockId, lineId, {
    values: selection.values,
    text: selection.other,
    teeth: selection.teeth,
    teethGroups: selection.groups,
    ...(selection.date && { date: selection.date }),
    ...(selection.marks?.length && { marks: selection.marks }),
    none: selection.none,
    ...(selection.numbers?.length && { numbers: selection.numbers }),
    ...(selection.tableRows?.length && { tableRows: selection.tableRows }),
  });

// Each field once: setting it as it is confirms its value and every part
// of it that waits.
export const confirmNoteAssumptions = (assumptions: NoteAssumption[]) => {
  const operations = assumptions
    .filter(
      (assumption, index) =>
        assumptions.findIndex((item) => item.blockId === assumption.blockId) ===
        index,
    )
    .map(({ blockId, lineId, selection }) =>
      confirmNoteBlock(blockId, lineId, selection),
    );
  return operations.length === 1
    ? operations[0]
    : combineNoteOperations(operations);
};

// Empty and locked against AI; on a text block, the template wording.
export const clearNoteBlock = (blockId: string, lineId: string) =>
  blockChange(blockId, lineId, { Clear: true });

// Drops the clinician's answer: AI may fill the block again.
export const releaseNoteBlock = (blockId: string, lineId: string) =>
  blockChange(blockId, lineId, { Release: true });

// A text block's wording removed: an empty manual text, which prints
// nothing until it is released.
export const removeNoteWording = (blockId: string, lineId: string) =>
  blockChange(blockId, lineId, { Text: '' });

// The line reset: its values cleared, so its options open again and the
// live fill does not write them back, and its removed wording released, so
// the template wording prints again; one request.
export const resetNoteLine = (
  lineId: string,
  { values, wordings }: NoteLineResetParts,
) =>
  combineNoteOperations([
    ...values.map((blockId) => clearNoteBlock(blockId, lineId)),
    ...wordings.map((blockId) => releaseNoteBlock(blockId, lineId)),
  ]);

// Brings back the AI answer a manual one displaced.
export const revertNoteBlock = (blockId: string, lineId: string) =>
  blockChange(blockId, lineId, { Revert: true });

export const applyNoteCorrection = (blockId: string, lineId: string) =>
  blockChange(blockId, lineId, { ApplyCorrection: true });

export const dismissNoteCorrection = (blockId: string, lineId: string) =>
  blockChange(blockId, lineId, { DismissCorrection: true });

// An own-option change keys itself: replacing an unsent one would lose the
// option it adds or removes.
const ownOptionChange = (
  blockId: string,
  lineId: string,
  change: Pick<
    PartialMessage<DocumentAnswerChange>,
    'AddOwnOption' | 'RemoveOwnOption'
  >,
  text: string,
): NoteOperation => ({
  ...blockChange(blockId, lineId, { ...change, Text: text }),
  key: uniqueKey(),
});

// "Other": the text becomes a new option of the block in this note, chosen
// by the block's rule.
export const addNoteOwnOption = (
  blockId: string,
  lineId: string,
  text: string,
) => ownOptionChange(blockId, lineId, { AddOwnOption: true }, text);

// Removes an own option of the block; a value holding it loses it.
export const removeNoteOwnOption = (
  blockId: string,
  lineId: string,
  text: string,
) => ownOptionChange(blockId, lineId, { RemoveOwnOption: true }, text);

export const changeNoteLine = (
  lineId: string,
  op: NoteLineOp,
  note = '',
): NoteOperation => ({
  key:
    op === NoteLineOp.NoteLineOpSetNote || op === NoteLineOp.NoteLineOpClearNote
      ? `note:${lineId}`
      : uniqueKey(),
  update: { LineChanges: [{ LineID: lineId, Op: op, Note: note }] },
  lineIds: [lineId],
});

// Text typed into the note's document: the whole text of a text block or of
// a line's note. An emptied wording (`def` a text block's) is removed, so
// its line prints nothing in its place; any other emptied text is cleared.
export const typeNoteText = (
  edit: Exclude<NoteTextEdit, { kind: 'choiceValue' }>,
  def?: NoteBlockDef,
): NoteOperation => {
  if (edit.kind === 'lineNote') {
    return {
      ...(edit.text
        ? changeNoteLine(edit.lineId, NoteLineOp.NoteLineOpSetNote, edit.text)
        : changeNoteLine(edit.lineId, NoteLineOp.NoteLineOpClearNote)),
      typing: true,
    };
  }
  if (!edit.text && def?.Kind.case === 'Text') {
    return { ...removeNoteWording(edit.blockId, edit.lineId), typing: true };
  }
  return {
    ...(edit.text
      ? setNoteBlock(edit.blockId, edit.lineId, { text: edit.text })
      : clearNoteBlock(edit.blockId, edit.lineId)),
    typing: true,
  };
};

// Skips the lines left to fill in every given section, in one request.
export const skipRemainingNoteLines = (
  sections: Pick<SurfaceSection, 'id' | 'unresolvedLineIds'>[],
): NoteOperation => {
  const open = sections.filter((section) => section.unresolvedLineIds.length);
  return {
    key: uniqueKey(),
    update: {
      SectionChanges: open.map((section) => ({
        SectionID: section.id,
        Op: NoteSectionOp.NoteSectionOpSkipRemaining,
      })),
    },
    lineIds: open.flatMap((section) => section.unresolvedLineIds),
  };
};

export const setNoteLineOrder = (
  sectionId: string,
  lineIds: string[],
): NoteOperation => ({
  key: `order:${sectionId}`,
  update: {
    SectionChanges: [
      {
        SectionID: sectionId,
        Op: NoteSectionOp.NoteSectionOpSetLineOrder,
        LineIDs: lineIds,
      },
    ],
  },
  lineIds: [],
});

export const resetNoteLineOrder = (sectionId: string): NoteOperation => ({
  key: `order:${sectionId}`,
  update: {
    SectionChanges: [
      { SectionID: sectionId, Op: NoteSectionOp.NoteSectionOpResetLineOrder },
    ],
  },
  lineIds: [],
});

export const addNoteLine = (
  sectionId: string,
  afterLineId: string,
  text: string,
): NoteOperation => ({
  key: uniqueKey(),
  update: {
    AddLines: [{ SectionID: sectionId, AfterLineID: afterLineId, Text: text }],
  },
  lineIds: afterLineId ? [afterLineId] : [],
});

export const changeNoteAddedBlock = (
  blockId: string,
  lineId: string,
  op: NoteAddedBlockOp,
): NoteOperation => ({
  key: uniqueKey(),
  update: { AddedBlockChanges: [{ BlockID: blockId, Op: op }] },
  lineIds: [lineId],
});

// Block-note operations go out at once: their inputs commit on Enter or
// blur, so there is no typing to wait for.
export const toNoteEdit = (
  operation: NoteOperation,
  onStored?: (answers: DocumentAnswers) => void,
): AnswerEdit => ({
  key: operation.key,
  note: operation.update,
  pending: { note: operation.update },
  immediate: true,
  onStored,
});

// The operation that brings a block back to the answer it had before the
// step that wrote `written`. Release drops a manual answer only, so a value
// AI wrote on an empty block is cleared; the AI value stays for Revert.
const restoreNoteAnswer = (
  blockId: string,
  lineId: string,
  prior: DocumentAnswer | undefined,
  written: DocumentAnswer | undefined,
) => {
  if (!prior) {
    return written?.Source === DocumentAnswerSource.DocumentAnswerSourceAI
      ? clearNoteBlock(blockId, lineId)
      : releaseNoteBlock(blockId, lineId);
  }
  if (prior.Source === DocumentAnswerSource.DocumentAnswerSourceAI) {
    return revertNoteBlock(blockId, lineId);
  }
  if (prior.Cleared) {
    return clearNoteBlock(blockId, lineId);
  }
  return setNoteBlock(blockId, lineId, {
    values: prior.Values,
    text: prior.Text,
    teeth: prior.Teeth,
    teethGroups: prior.TeethGroups,
    ...(prior.Date && { date: prior.Date }),
    ...(prior.Marks.length > 0 && {
      marks: prior.Marks.flatMap(({ Mark }) => (Mark ? [Mark] : [])),
    }),
    none: prior.None,
    ...(prior.Numbers.length > 0 && { numbers: prior.Numbers }),
    ...(prior.TableRows.length > 0 && {
      tableRows: readNoteTableRows(prior.TableRows),
    }),
  });
};

const LINE_OP_INVERSES: Partial<Record<NoteLineOp, NoteLineOp>> = {
  [NoteLineOp.NoteLineOpDelete]: NoteLineOp.NoteLineOpRestore,
  [NoteLineOp.NoteLineOpRestore]: NoteLineOp.NoteLineOpDelete,
  [NoteLineOp.NoteLineOpSkip]: NoteLineOp.NoteLineOpUnskip,
  [NoteLineOp.NoteLineOpUnskip]: NoteLineOp.NoteLineOpSkip,
};

const viewSection = (answers: NoteAnswers, sectionId: string) =>
  answers.View?.Sections.find((section) => section.SectionID === sectionId);

const lineStatus = (answers: NoteAnswers, sectionId: string, lineId: string) =>
  viewSection(answers, sectionId)?.Lines.find((line) => line.LineID === lineId)
    ?.Status;

// The inverse of one block change. "Other" takes back the option it added
// and a removal brings its option back, at the end of the options; then the
// block gets the answer it had.
const invertNoteBlockChange = (
  change: PartialMessage<DocumentAnswerChange>,
  lineId: string,
  before: NoteAnswers,
  after: NoteAnswers,
): NoteOperation => {
  const blockId = change.Key?.BlockID ?? '';
  const prior = findNoteAnswer(before, blockId);
  const written = findNoteAnswer(after, blockId);
  const optionsBefore = findNoteOwnOptions(before, blockId);
  const optionsAfter = findNoteOwnOptions(after, blockId);
  if (change.AddOwnOption) {
    return combineNoteOperations([
      ...optionsAfter
        .filter((option) => !optionsBefore.includes(option))
        .map((option) => removeNoteOwnOption(blockId, lineId, option)),
      restoreNoteAnswer(blockId, lineId, prior, written),
    ]);
  }
  if (change.RemoveOwnOption) {
    // The option brought back is chosen until the answer is restored, so an
    // empty block is released, not cleared.
    return combineNoteOperations([
      ...optionsBefore
        .filter((option) => !optionsAfter.includes(option))
        .map((option) => addNoteOwnOption(blockId, lineId, option)),
      restoreNoteAnswer(blockId, lineId, prior, undefined),
    ]);
  }
  return restoreNoteAnswer(blockId, lineId, prior, written);
};

const invertNoteLineChange = (
  {
    LineID: lineId = '',
    Op: op = NoteLineOp.NoteLineOpInvalidValue,
  }: PartialMessage<NoteLineChange>,
  before: NoteAnswers,
): NoteOperation | null => {
  const inverse = LINE_OP_INVERSES[op];
  if (inverse) {
    return changeNoteLine(lineId, inverse);
  }
  const prior = before.LineNotes.find((note) => note.LineID === lineId);
  if (prior) {
    return changeNoteLine(lineId, NoteLineOp.NoteLineOpSetNote, prior.Text);
  }
  return op === NoteLineOp.NoteLineOpSetNote
    ? changeNoteLine(lineId, NoteLineOp.NoteLineOpClearNote)
    : null;
};

// The inverse of an operation, built from the answers before it and the
// answers the server stored with it. Dismissing a correction hint has no
// inverse: the hint is gone. Block changes are taken back last to first: a
// later value may sit on a line an earlier one showed; the line changes of
// the same request, as a deletion across lines sends, with them.
export const invertNoteOperation = (
  { update, lineIds }: NoteOperation,
  before: NoteAnswers,
  after: NoteAnswers,
): NoteOperation | null => {
  const changes = update.Changes ?? [];
  const lineChanges = update.LineChanges ?? [];
  if (changes.length || lineChanges.length) {
    const inverses = [
      ...changes
        .flatMap((change, index) =>
          change.DismissCorrection
            ? []
            : [
                invertNoteBlockChange(
                  change,
                  lineIds[index] ?? '',
                  before,
                  after,
                ),
              ],
        )
        .reverse(),
      ...lineChanges.flatMap(
        (change) => invertNoteLineChange(change, before) ?? [],
      ),
    ];
    return inverses.length > 1
      ? combineNoteOperations(inverses)
      : inverses[0] ?? null;
  }

  const [blockChangeOp] = update.AddedBlockChanges ?? [];
  if (blockChangeOp) {
    return changeNoteAddedBlock(
      blockChangeOp.BlockID ?? '',
      lineIds[0] ?? '',
      blockChangeOp.Op === NoteAddedBlockOp.NoteAddedBlockOpRemove
        ? NoteAddedBlockOp.NoteAddedBlockOpRestore
        : NoteAddedBlockOp.NoteAddedBlockOpRemove,
    );
  }

  const [addLine] = update.AddLines ?? [];
  if (addLine) {
    const known = new Set(before.AddedLines.map((added) => added.Line?.ID));
    const added = after.AddedLines.filter(
      (item) =>
        item.SectionID === addLine.SectionID &&
        item.Source === DocumentAnswerSource.DocumentAnswerSourceManual &&
        !known.has(item.Line?.ID),
    ).at(-1);
    return added?.Line
      ? changeNoteLine(added.Line.ID, NoteLineOp.NoteLineOpDelete)
      : null;
  }

  const sectionChanges = update.SectionChanges ?? [];
  const [sectionChange] = sectionChanges;
  if (!sectionChange) {
    return null;
  }
  const sectionId = sectionChange.SectionID ?? '';
  if (sectionChange.Op === NoteSectionOp.NoteSectionOpSkipRemaining) {
    const skipped = sectionChanges.flatMap(({ SectionID = '' }) =>
      (viewSection(after, SectionID)?.Lines ?? []).filter(
        (line) =>
          line.Status === NoteLineStatus.NoteLineStatusSkipped &&
          lineStatus(before, SectionID, line.LineID) !==
            NoteLineStatus.NoteLineStatusSkipped,
      ),
    );
    return skipped.length
      ? {
          key: uniqueKey(),
          update: {
            LineChanges: skipped.map((line) => ({
              LineID: line.LineID,
              Op: NoteLineOp.NoteLineOpUnskip,
            })),
          },
          lineIds: skipped.map((line) => line.LineID),
        }
      : null;
  }
  const previous = viewSection(before, sectionId);
  return previous?.CustomLineOrder
    ? setNoteLineOrder(
        sectionId,
        previous.Lines.map((line) => line.LineID),
      )
    : resetNoteLineOrder(sectionId);
};

// The client's own undo stack of the whole note
// (block-notes-manual-operations.md, "Undo"), undone in the order the steps
// were made: every entry keeps the answers before the operation, and the
// stored answers once the server accepted it.
export type NoteHistoryEntry = {
  id: string;
  operation: NoteOperation;
  before: NoteAnswers;
  after?: NoteAnswers;
  // An AI step, recorded once its job finished: edits sent while it ran are
  // older entries, so its undo spares every block changed since.
  aiStep?: boolean;
};

export type NoteHistory = {
  undo: NoteHistoryEntry[];
  redo: NoteHistoryEntry[];
};

export const EMPTY_NOTE_HISTORY: NoteHistory = { undo: [], redo: [] };

// An edit that replaces an unsent edit of the same block is one step, and so
// is text typed into the text the latest step typed into: undo takes it back
// to what it was before the typing. A new step drops the redo.
export const recordNoteOperation = (
  history: NoteHistory,
  entry: NoteHistoryEntry,
): NoteHistory => {
  const top = history.undo.at(-1);
  const sameKey = !!top && top.operation.key === entry.operation.key;
  const merges =
    sameKey &&
    (!top.after || (!!top.operation.typing && !!entry.operation.typing));
  return {
    undo: merges
      ? [
          ...history.undo.slice(0, -1),
          { ...top, id: entry.id, operation: entry.operation },
        ]
      : [...history.undo, entry],
    redo: [],
  };
};

export const markNoteOperationStored = (
  history: NoteHistory,
  entryId: string,
  after: NoteAnswers,
): NoteHistory => {
  const mark = (entries: NoteHistoryEntry[]) =>
    entries.map((entry) =>
      entry.id === entryId ? { ...entry, after } : entry,
    );
  return { undo: mark(history.undo), redo: mark(history.redo) };
};

const writtenValue = (answer: DocumentAnswer | undefined) =>
  answer && {
    source: answer.Source,
    cleared: answer.Cleared,
    values: answer.Values,
    text: answer.Text,
    teeth: answer.Teeth,
    teethGroups: answer.TeethGroups,
    aiStepId: answer.AIStepID,
  };

const findViewLine = (answers: NoteAnswers, lineId: string) =>
  answers.View?.Sections.flatMap((section) => section.Lines).find(
    (line) => line.LineID === lineId,
  );

// A block on a line closed or hidden since takes no change.
const isLineOpen = (answers: NoteAnswers, lineId: string) => {
  const line = findViewLine(answers, lineId);
  return !line || isNoteLineShown(line);
};

// The part of a stored step its undo reverts now. A step is undone only
// after the later ones, so it reverts every block, unless it is an AI step:
// that one spares a block changed since and a line closed since.
export const getUndoableNoteOperation = (
  { operation, after, aiStep }: NoteHistoryEntry,
  current: NoteAnswers,
): NoteOperation => {
  const changes = operation.update.Changes ?? [];
  if (!aiStep || !after || !changes.length) {
    return operation;
  }
  const unchanged = changes.flatMap((change, index) => {
    const blockId = change.Key?.BlockID ?? '';
    const lineId = operation.lineIds[index] ?? '';
    return isEqual(
      writtenValue(findNoteAnswer(current, blockId)),
      writtenValue(findNoteAnswer(after, blockId)),
    ) && isLineOpen(current, lineId)
      ? [{ change, lineId }]
      : [];
  });
  return {
    ...operation,
    update: {
      ...operation.update,
      Changes: unchanged.map(({ change }) => change),
    },
    lineIds: unchanged.map(({ lineId }) => lineId),
  };
};

// The latest step the server stored.
export const findNoteHistoryEntry = (entries: NoteHistoryEntry[]) =>
  [...entries].reverse().find((entry) => !!entry.after);

// The step undo takes back: the latest one stored, or text just typed, whose
// inverse needs only the answers before it.
export const findNoteUndoEntry = (entries: NoteHistoryEntry[]) => {
  const latest = entries.at(-1);
  return latest?.operation.typing && !latest.after
    ? latest
    : findNoteHistoryEntry(entries);
};

export const moveNoteHistoryEntry = (
  history: NoteHistory,
  entryId: string,
  to: keyof NoteHistory,
  replacement: NoteHistoryEntry,
): NoteHistory => {
  const from = to === 'undo' ? 'redo' : 'undo';
  return {
    ...history,
    [from]: history[from].filter((entry) => entry.id !== entryId),
    [to]: [...history[to], replacement],
  };
};
